/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { Event, EventSubscription } from '@trustedshops/tswp-core-common';

class SubscribableStrategy {
  public createSubscription(async: Event<any>, updateLatestValue: any): EventSubscription<any> {
    return async.subscribe(updateLatestValue);
  }

  public dispose(subscription: EventSubscription<any>): void {
    subscription?.unsubscribe();
  }
}

/**
 * Unwraps a value from an asynchronous primitive.
 *
 * The `subscribe` pipe subscribes to an `Event` and returns the latest value it has
 * emitted. When a new value is emitted, the `async` pipe marks the component to be checked for
 * changes. When the component gets destroyed, the `async` pipe unsubscribes automatically to avoid
 * potential memory leaks.
 */
@Pipe({name: 'subscribe', pure: false, standalone: false,})
export class SubscribePipe implements OnDestroy, PipeTransform {
  //#region Private Fields
  private _latestValue: any = null;

  private _subscription: EventSubscription<any>|null = null;
  private _obj: Event<any>|null = null;
  private _strategy: SubscribableStrategy = null;
  //#endregion

  //#region Ctor
  public constructor(private _ref: ChangeDetectorRef) { }
  //#endregion

  //#region Public Methods
  public ngOnDestroy(): void {
    if (this._subscription) {
      this._dispose();
    }
  }

  public transform<T>(obj: null|undefined): null;
  public transform<T>(obj: Event<T>|null): T|null;
  public transform<T>(obj: Event<T>|null): T|null {
    if (!this._obj) {
      if (obj) {
        this._subscribe(obj);
      }
      return this._latestValue;
    }

    if (obj !== this._obj) {
      this._dispose();
      return this.transform(obj);
    }

    return this._latestValue;
  }
  //#endregion

  //#region Private Methods
  private _subscribe(obj: Event<any>): void {
    this._obj = obj;
    this._strategy = new SubscribableStrategy();
    this._subscription = this._strategy.createSubscription(
        obj, (value: any) => this._updateLatestValue(obj, value));
  }

  private _dispose(): void {
    this._strategy.dispose(this._subscription);
    this._latestValue = null;
    this._subscription = null;
    this._obj = null;
  }

  private _updateLatestValue(async: any, value: any): void {
    if (async === this._obj) {
      this._latestValue = value;
      this._ref.markForCheck();
    }
  }
  //#endregion
}
